import { useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { Users, TrendingUp, AlertTriangle, Clock } from "lucide-react";
import { HeroToday } from "@/components/dashboard/HeroToday";
import { WeekSummary } from "@/components/dashboard/WeekSummary";
import { WeekChart } from "@/components/dashboard/WeekChart";
import { RecoverCard } from "@/components/dashboard/RecoverCard";
import { DueTodayList } from "@/components/dashboard/DueTodayList";
import { MastersTable } from "@/components/dashboard/MastersTable";
import { SyncBar } from "@/components/dashboard/SyncBar";
import { GoalCard } from "@/components/dashboard/GoalCard";
import { LowCreditsAlert } from "@/components/dashboard/LowCreditsAlert";
import { TrialButton } from "@/components/TrialDialog";
import { ReguaCard } from "@/components/dashboard/ReguaCard";
import { SmsBalanceCard } from "@/components/dashboard/SmsBalanceCard";
import { AppStatsCard } from "@/components/dashboard/AppStatsCard";
import { BiaCard } from "@/components/dashboard/BiaCard";
import { FunnelCard } from "@/components/dashboard/FunnelCard";
import { WaGroupsCard } from "@/components/dashboard/WaGroupsCard";
import { PanelHealthCard } from "@/components/dashboard/PanelHealthCard";
import { ServiceDueCard } from "@/components/dashboard/ServiceDueCard";
import { LiveFeed } from "@/components/ops/LiveFeed";
import { useAuth } from "@/components/auth/AuthProvider";
import { OpsDashboard } from "@/components/ops/OpsDashboard";
import { MovementCard } from "@/components/dashboard/MovementCard";

const Kpi = ({ icon: Icon, label, value, sub, color, testid, delay }) => (
  <div data-testid={testid} className="rounded-2xl border border-[#1F293D] bg-[#111827] p-5 transition-colors hover:border-[#334155] fade-up" style={{ animationDelay: delay }}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="rounded-lg p-1.5" style={{ background: `${color}1A` }}><Icon className="h-4 w-4" style={{ color }} /></span>
    </div>
    <div className="mt-3 font-mono text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

const AccountTabs = ({ accounts, value, onChange }) => (
  <div data-testid="dashboard-account-filter" className="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-[#1F293D] bg-[#0F141F] p-1">
    {[{ account: "" }, ...accounts].map((a) => (
      <button key={a.account || "all"} data-testid={`account-tab-${a.account || "todas"}`} onClick={() => onChange(a.account)}
        className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${value === a.account ? "bg-sky-500 text-slate-950" : "text-slate-400 hover:bg-[#1A2234] hover:text-slate-100"}`}>
        {a.account || "Todas"}
      </button>
    ))}
  </div>
);

function AdminDashboard() {
  const [d, setD] = useState(null);
  const [account, setAccount] = useState("");
  const [accounts, setAccounts] = useState([]);

  const load = () => api.get("/dashboard", { params: account ? { account } : {} }).then((r) => {
    setD(r.data);
    if (!account) setAccounts((r.data.masters || []).map((m) => ({ account: m.account })));
  });
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <div className="text-slate-400">Carregando...</div>;
  const r = d.recover;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-100">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">Receita e cobrança · {account || "todas as contas"}</p>
        </div>
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
          <TrialButton />
          <AccountTabs accounts={accounts} value={account} onChange={setAccount} />
        </div>
      </div>

      <ServiceDueCard />

      <GoalCard goal={d.goal} onSaved={load} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <HeroToday today={d.today} next={d.next_days || []} />
        <WeekSummary week={d.week} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testid="kpi-mrr" icon={TrendingUp} color="#34D399" label="Receita mensal (MRR)" value={fmtBRL(d.mrr)} sub={`ticket médio ${fmtBRL(d.ticket_medio)} /mês`} delay="0ms" />
        <Kpi testid="kpi-active" icon={Users} color="#38BDF8" label="Clientes ativos" value={d.active_paying.toLocaleString("pt-BR")} sub={`${d.counts.teste.toLocaleString("pt-BR")} em teste · ${d.whatsapp_count.toLocaleString("pt-BR")} com WhatsApp`} delay="60ms" />
        <Kpi testid="kpi-overdue" icon={AlertTriangle} color="#F87171" label="A recuperar (≤30 dias)" value={fmtBRL(r.d7.amount + r.d30.amount)} sub={`${(r.d7.count + r.d30.count).toLocaleString("pt-BR")} logins vencidos recentes`} delay="120ms" />
        <Kpi testid="kpi-due-soon" icon={Clock} color="#FBBF24" label="Vencem em 3 dias" value={fmtBRL(d.due_soon_amount)} sub={`${d.counts.a_vencer.toLocaleString("pt-BR")} logins`} delay="180ms" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <WeekChart days={d.week.days} />
        <RecoverCard recover={r} />
      </div>

      <MovementCard />

      <PanelHealthCard />
      <SmsBalanceCard balance={d.sms_balance} onRefresh={load} />
      <AppStatsCard />
      <FunnelCard />
      <BiaCard />
      <WaGroupsCard />

      <LowCreditsAlert masters={d.masters} threshold={d.low_credits_threshold || 500} />

      <SyncBar accounts={d.api_accounts || []} renewals={d.renewals_total || 0} onRefresh={load} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <DueTodayList items={d.due_today} />
        <MastersTable masters={d.masters} current={account} onPick={setAccount} />
      </div>

      <ReguaCard rows={d.regua || []} />
      <LiveFeed />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return user?.role === "admin" ? <AdminDashboard /> : <OpsDashboard />;
}
